import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import SelectClientStep from "../../components/event_media/SelectClientStep";
import SelectTicketStep from "../../components/event_media/SelectTicketStep";
import AgentConferenceRegistration from "./event_registration/AgentConferenceRegistration";
import AgentTradeMissionRegistration from "./event_registration/AgentTradeMissionRegistration";

const REG_STEP = { CLIENT: "client", TICKET: "ticket", FORM: "form" };

export default function EventRegistrationRouter() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regStep, setRegStep] = useState(REG_STEP.CLIENT);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!slug) {
      navigate("/event-media-sales");
      return;
    }
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(slug);
        const event = response.event ?? response.data?.event ?? response.data?.data?.event ?? response.data;
        if (!response.status || !event) {
          toast.error(response.message || "Failed to load event");
          navigate(`/event-media-sales/event-details/${slug}`);
          return;
        }
        setEventData(event);
        const types = event.event_type || [];
        const isConference = types.some((t) => String(t || "").toLowerCase().includes("conference"));
        setEventType(isConference ? "conference" : "trade_mission");
      } catch (err) {
        toast.error(err?.message || "An error occurred");
        navigate(`/event-media-sales/event-details/${slug}`);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug, navigate]);

  const handleClientSelected = (payload) => {
    setSelectedClient(payload);
    if (eventType === "conference" && eventData?.ticket_types?.length > 0) {
      setRegStep(REG_STEP.TICKET);
      setSelectedTicket(null);
      setQuantity(1);
    } else {
      setRegStep(REG_STEP.FORM);
      setSelectedTicket(null);
      setQuantity(1);
    }
  };

  const handleTicketContinue = (ticketSelection) => {
    if (ticketSelection) {
      setSelectedTicket(ticketSelection.ticketType);
      setQuantity(ticketSelection.quantity ?? 1);
    }
    setRegStep(REG_STEP.FORM);
  };

  const handleBackFromTicket = () => {
    setRegStep(REG_STEP.CLIENT);
    setSelectedClient(null);
    setSelectedTicket(null);
    setQuantity(1);
  };

  const handleBackFromClient = () => {
    navigate(`/event-media-sales/event-details/${slug}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="flex flex-col gap-4 items-center">
          <div className="w-12 h-12 rounded-full border-2 border-orange-200 border-t-orange-500 animate-spin" />
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!eventData) return null;

  if (regStep === REG_STEP.CLIENT) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Register for event</h1>
            <p className="text-gray-600 mt-1">{eventData.event_title}</p>
          </div>
          <SelectClientStep
            onClientSelected={handleClientSelected}
            onBack={handleBackFromClient}
          />
        </div>
      </div>
    );
  }

  if (regStep === REG_STEP.TICKET && eventType === "conference") {
    return (
      <SelectTicketStep
        event={eventData}
        selectedTicket={selectedTicket}
        quantity={quantity}
        onSelectTicket={setSelectedTicket}
        onQuantityChange={setQuantity}
        onContinue={handleTicketContinue}
        onBack={handleBackFromTicket}
        singleTicketOnly
      />
    );
  }

  if (regStep === REG_STEP.FORM) {
    if (eventType === "conference") {
      return (
        <AgentConferenceRegistration
          eventData={eventData}
          slug={slug}
          selectedClient={selectedClient}
          selectedTicket={selectedTicket}
          quantity={quantity}
          onBack={() => {
            setRegStep(eventData?.ticket_types?.length ? REG_STEP.TICKET : REG_STEP.CLIENT);
            if (!eventData?.ticket_types?.length) setSelectedClient(null);
          }}
        />
      );
    }
    return (
      <AgentTradeMissionRegistration
        eventData={eventData}
        slug={slug}
        selectedClient={selectedClient}
        onBack={() => {
          setRegStep(REG_STEP.CLIENT);
          setSelectedClient(null);
        }}
      />
    );
  }

  return null;
}
